import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import constants from 'constants/constants';
import { lowerCase, startCase } from 'lodash';
import moment from 'moment-timezone';
import colorsVars from 'styles/colors.module.scss';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { cardsDataQueryKey } from 'api/queries/cardsData.query';
import { ISingleSmallCardResponse, listCardsDataQueryKey } from 'api/queries/getListFilteredCards.query';

import { ActivityCell } from 'components/common/Tables/Cells/ActivityCell/ActivityCell';
import { ActivityIdCell } from 'components/common/Tables/Cells/ActivityIdCell/ActivityIdCell';
import { AssigneeCell } from 'components/common/Tables/Cells/AssigneeCell/AssigneeCell';
import { BasicDateCell } from 'components/common/Tables/Cells/BasicDateCell/BasicDateCell';
import { DateCell } from 'components/common/Tables/Cells/DateCell/DateCell';
import { DetailsCell } from 'components/common/Tables/Cells/DetailsCell/DetailsCell';
import { DynamicChipsCell } from 'components/common/Tables/Cells/DynamicChipsCell/DynamicChipsCell';
import { IndicatorsCell } from 'components/common/Tables/Cells/IndicatorsCell/IndicatorsCell';
import { StatusCell } from 'components/common/Tables/Cells/StatusCell/StatusCell';
import { TooltipCellWithCopy } from 'components/common/Tables/Cells/TooltipCellWithCopy/TooltipCellWithCopy';
import { VarianceCell } from 'components/common/Tables/Cells/VarianceCell/VarianceCell';
import { SubmittedDurationCompleteCell } from 'components/common/Tables/Cells/submittedDurationCompleteCell/SubmittedDurationCompleteCell';
import { customSortWithNullV8 } from 'components/common/Tables/Tables.utils';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';
import { TrackedUntrackedActionCellWrapper } from 'components/common/TrackedUntrackedAction/trackedUntrackedActionCellWrapper/trackedUntrackedActionCellWrapper';

import CriticalityScoreCell from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/CommonComponents/Cells/criticalityScoreCell';
import {
    activityCodesTag,
    milestoneFragnetTag,
    wbsTag,
    widgetFilterTitle,
} from 'components/Dashboards/Project/Components/CustomDashboard/customDashboard.utils';
import { CircularProgressWithLabel } from 'components/DesignSystem/CircularProgressWithLabel';
import { CustomizedTooltip } from 'components/common';

const { color6: orange, textColor, delayDriverColor } = colorsVars;

const invalidateKeys = [cardsDataQueryKey, listCardsDataQueryKey];

export const initialState: ITableColumnState = {
    hiddenColumns: [
        'currentStartDate',
        'currentFinishDate',
        'lateFinish',
        'lateStart',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'completedDuration',
        'durationOverrun',
        'numOfPredecessors',
        'numOfSuccessors',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'tagList',
        'originalStatus',
        'baselineStartDate',
        'baselineFinishDate',
        'resourceNameList',
        'allBudgetResource',
        'allActualResource',
    ],
    columnOrder: [
        'activity',
        'activityId',
        'riskIndicators',
        'status',
        'assignee',
        'submittedDurationComplete',
        'activityType',
        'details',
        'criticalPath',
        'float',
        'startDate',
        'finishDate',
        'baselineStartDate',
        'baselineFinishDate',
        'remainingDuration',
        'parentName',
        'grandParentName',
        'criticalityScore',
        'tagList',
        'lateStart',
        'lateFinish',
        'plannedStartDate',
        'plannedFinishDate',
        'actualStartDate',
        'actualFinishDate',
        'plannedDuration',
        'actualDuration',
        'completedDuration',
        'durationOverrun',
        'numOfPredecessors',
        'numOfSuccessors',
        'projectWeekStart',
        'projectWeekFinish',
        'projectMonthStart',
        'projectMonthFinish',
        'originalStatus',
    ],
};

export const columnsV8: ColumnDef<ISingleSmallCardResponse>[] = [
    {
        header: 'Activity',
        accessorKey: 'title',
        ...columnsCommonConfig.activity,
        cell: ({ row, table }) => {
            const { taskVersionHashCode, isTracked, isVirtual } = row.original;
            const activityName = row.original.title;
            const taskId = row.original.id;
            const { getToggleSelectedHandler } = row;
            const { toggleAllRowsSelected } = table;

            return (
                <ActivityCell
                    activityName={activityName}
                    taskId={taskId}
                    isVirtual={isVirtual}
                    toggleRowSelected={getToggleSelectedHandler()}
                    toggleAllRowsSelected={toggleAllRowsSelected}
                    trackedUntrackedActionCell={
                        <TrackedUntrackedActionCellWrapper
                            isTracked={isTracked}
                            taskVersionHashCode={taskVersionHashCode}
                            invalidateQueriesKey={invalidateKeys}
                        />
                    }
                />
            );
        },
    },
    {
        header: 'Activity id',
        accessorKey: 'title',
        ...columnsCommonConfig.activityId,
        cell: ({ row }) => {
            const activityId = row.original.type.id === 2 ? row.original.taskActivityId || '' : `C-${row.original.id}`;
            return <ActivityIdCell activityId={activityId} />;
        },
    },
    {
        header: 'Risk indicators',
        accessorKey: 'title',
        ...columnsCommonConfig.riskIndicators,
        cell: ({ row }) => {
            const cardIndicators = row.original.cardIndicators;
            return <IndicatorsCell cardIndicators={cardIndicators} />;
        },
    },
    {
        header: 'Status',
        accessorKey: 'status.label',
        ...columnsCommonConfig.status,
        cell: ({ row }) => {
            const { status, isVirtual, originalStatus, id, isInterimChanged } = row.original;

            const cardType = row.original.type.name;
            const category = row.original.category;

            return (
                <StatusCell
                    status={status}
                    isVirtual={isVirtual}
                    taskId={id}
                    originalStatus={originalStatus}
                    invalidateQueriesKey={invalidateKeys}
                    cardType={cardType}
                    category={category}
                    isInterimChanged={isInterimChanged}
                />
            );
        },
    },
    {
        header: 'Assignee',
        accessorKey: 'status.label',
        ...columnsCommonConfig.assignee,
        cell: ({ row }) => {
            const { assignee, isVirtual, id } = row.original;

            return (
                <AssigneeCell
                    assignee={assignee}
                    taskId={id}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                />
            );
        },
    },
    {
        header: '% Complete',
        ...columnsCommonConfig.submittedDurationComplete,
        accessorKey: 'submittedDurationComplete',
        cell: ({ getValue }) => {
            const value = getValue();
            return <SubmittedDurationCompleteCell value={value} />;
        },
    },
    {
        header: 'Activity Type',
        ...columnsCommonConfig.activityType,
        accessorFn: (original) =>
            original.type.name === 'PROGRAMME_ACTIVITY' ? original.taskActivityType : original.type.label,
        cell: ({ getValue }) => (
            <TooltipCellWithCopy
                text={getValue<
                    ISingleSmallCardResponse['taskActivityType'] | ISingleSmallCardResponse['type']['label']
                >()}
            />
        ),
    },
    {
        header: 'Details/Last Comment',
        accessorKey: 'details',
        ...columnsCommonConfig.details,
        cell: ({ row }) => {
            const { cardLastComment, details, id, isVirtual, isGenAiDetails } = row.original;
            return (
                <DetailsCell
                    details={details}
                    cardLastComment={cardLastComment}
                    id={id}
                    invalidateQueriesKey={invalidateKeys}
                    isVirtual={isVirtual}
                    isGenAiDetails={isGenAiDetails}
                />
            );
        },
    },
    {
        header: 'Critical Path',
        accessorFn: (row) => (row.cardIndicators.isCriticalPath.isIndicatorOn ? 'Yes' : 'No'),
        ...columnsCommonConfig.criticalPath,
    },
    {
        header: 'Start Date',
        ...columnsCommonConfig.startDate,
        accessorKey: 'startDate',
        cell: ({ getValue, row }) => {
            const changeMapDate = row.original.startVariance;
            const comparedVersion = row.original.baselineStartDate;
            return (
                <DateCell
                    latestVersionDate={getValue<ISingleSmallCardResponse['startDate']>()}
                    changeMapDate={changeMapDate}
                    comparedVersion={comparedVersion}
                    isChangeMap={true}
                />
            );
        },
    },
    {
        header: 'Finish Date',
        ...columnsCommonConfig.finishDate,
        accessorKey: 'endDate',
        cell: ({ getValue, row }) => {
            const changeMapDate = row.original.finishVariance;
            const comparedVersion = row.original.baselineFinishDate;
            return (
                <DateCell
                    latestVersionDate={getValue<ISingleSmallCardResponse['endDate']>()}
                    changeMapDate={changeMapDate}
                    comparedVersion={comparedVersion}
                    isChangeMap={true}
                />
            );
        },
    },
    {
        header: 'Baseline Start Date ',
        accessorKey: 'baselineStartDate',
        ...columnsCommonConfig.baselineStartDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['baselineStartDate']>()} />;
        },
    },
    {
        header: 'Baseline Finish Date',
        accessorKey: 'baselineFinishDate',
        ...columnsCommonConfig.baselineFinishDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['baselineFinishDate']>()} />;
        },
    },
    {
        header: 'Remaining Duration',
        ...columnsCommonConfig.remainingDuration,
        accessorKey: 'remainingDurationInDays',
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['remainingDurationInDays']>();
            return value !== null ? Math.ceil(value) : '';
        },
    },
    {
        header: 'Immediate Parent Name ',
        ...columnsCommonConfig.parentName,
        accessorKey: 'parentActivity',
        cell: ({ getValue }) => <TooltipCellWithCopy text={getValue<ISingleSmallCardResponse['parentActivity']>()} />,
    },
    {
        header: 'Grand Parent Name',
        ...columnsCommonConfig.grandParentName,
        accessorKey: 'grandParentActivity',
        cell: ({ getValue }) => (
            <TooltipCellWithCopy text={getValue<ISingleSmallCardResponse['grandParentActivity']>() || ''} />
        ),
    },
    {
        header: 'Criticality Score',
        accessorKey: 'criticalityScore',
        ...columnsCommonConfig.criticalityScore,
        cell: ({ getValue, row }) => {
            const delayDriver = row.original.cardIndicators.isDelayDriver.isIndicatorOn;
            const style = delayDriver ? { color: delayDriverColor, fontWeight: 600 } : {};
            const numOfPredecessors = row.original.numOfPredecessors;
            const numOfSuccessors = row.original.numOfSuccessors;
            const precedingActivities = row.original.precedingActivities;
            const succeedingActivites = row.original.succeedingActivites;
            const precedingRisk = row.original.precedingRisk;
            const succeedingRisk = row.original.succeedingRisk;
            return (
                <CriticalityScoreCell
                    succeedingRisk={succeedingRisk}
                    precedingRisk={precedingRisk}
                    numOfSuccessors={numOfSuccessors}
                    numOfPredecessors={numOfPredecessors}
                    succeedingActivites={succeedingActivites}
                    precedingActivities={precedingActivities}
                    style={style}
                    value={getValue<ISingleSmallCardResponse['criticalityScore']>()}
                />
            );
        },
        sortingFn: 'basic',
    },
    {
        header: 'Float',
        accessorKey: 'float',
        ...columnsCommonConfig.float,
        sortingFn: customSortWithNullV8,
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['float']>();
            return value !== null ? Math.ceil(value) : '';
        },
    },
    {
        header: 'Tags',
        accessorKey: 'tagList',
        ...columnsCommonConfig.tagList,
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['tagList']>();
            const list = value.map((item) => item.name);
            return list.length > 0 ? <DynamicChipsCell list={list} /> : null;
        },
    },
    {
        header: 'Resources',
        accessorKey: 'allResourceNameList',
        ...columnsCommonConfig.resourceNameList,
        cell: ({ getValue }) => {
            const list = getValue<ISingleSmallCardResponse['allResourceNameList']>();
            return list && list.length > 0 ? <DynamicChipsCell list={list} type={'unAssignee'} /> : null;
        },
    },

    {
        header: 'Resource Budget',
        accessorKey: 'allBudgetResourceUnit',
        ...columnsCommonConfig.allBudgetResource,
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['allBudgetResourceUnit']>();
            return value !== null ? Math.ceil(value) : '';
        },
    },

    {
        header: 'Resource Actual',
        accessorKey: 'allActualResourceUnit',
        ...columnsCommonConfig.allActualResource,
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['allActualResourceUnit']>();
            return value !== null ? Math.ceil(value) : '';
        },
    },

    {
        header: 'Start Variance',
        ...columnsCommonConfig.lateStart,
        accessorKey: 'startVariance',
        cell: ({ getValue }) => {
            const value = getValue<number>();
            return <VarianceCell value={Math.ceil(value)} />;
        },
    },
    {
        header: 'Finish Variance',
        ...columnsCommonConfig.lateFinish,
        accessorKey: 'finishVariance',
        cell: ({ getValue }) => {
            const value = getValue<number>();
            return <VarianceCell value={Math.ceil(value)} />;
        },
    },
    {
        header: 'Planned Start Date ',
        accessorKey: 'plannedStartDate',
        ...columnsCommonConfig.plannedStartDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['plannedStartDate']>()} />;
        },
    },
    {
        header: 'Planned Finish Date',
        accessorKey: 'plannedFinishDate',
        ...columnsCommonConfig.plannedFinishDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['plannedFinishDate']>()} />;
        },
    },
    {
        header: 'Actual Start Date',
        accessorKey: 'actualStartDate',
        ...columnsCommonConfig.actualStartDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['actualStartDate']>()} />;
        },
    },
    {
        header: 'Actual Finish Date',
        accessorKey: 'actualFinishDate',
        ...columnsCommonConfig.actualFinishDate,
        cell: ({ getValue }) => {
            return <BasicDateCell value={getValue<ISingleSmallCardResponse['actualFinishDate']>()} />;
        },
    },
    {
        header: 'Planned Duration',
        ...columnsCommonConfig.plannedDuration,
        accessorKey: 'plannedDurationInDays',
        cell: ({ getValue }) => {
            const value = getValue<number | null>();
            return value !== null ? Math.ceil(value) : '';
        },
    },
    {
        header: 'Actual Duration',
        ...columnsCommonConfig.actualDuration,
        accessorKey: 'actualDurationInDays',
        cell: ({ getValue }) => {
            const value = getValue<number | null>();
            return value !== null ? Math.ceil(value) : '';
        },
    },
    {
        header: 'Current Duration',
        ...columnsCommonConfig.completedDuration,
        accessorKey: 'completedDurationInDays',
        cell: ({ getValue }) => {
            const value = getValue<number | null>();
            return value !== null ? Math.ceil(value) : '';
        },
    },
    {
        header: 'Current Duration Overrun',
        ...columnsCommonConfig.durationOverrun,
        accessorKey: 'completedDurationOverrun',
        cell: ({ getValue }) => {
            const value = getValue<number>();
            const color = value > 0 ? orange : textColor;
            return <span style={{ color }}>{Math.ceil(value)}</span>;
        },
    },
    {
        header: 'Pred.',
        accessorKey: 'numOfPredecessors',
        ...columnsCommonConfig.numOfPredecessors,
    },
    {
        header: 'Succ.',
        accessorKey: 'numOfSuccessors',
        ...columnsCommonConfig.numOfSuccessors,
    },
    {
        header: 'Project Week Start ',
        accessorKey: 'projectWeekStart',
        ...columnsCommonConfig.projectWeekStart,
    },
    {
        header: 'Project Week Finish',
        accessorKey: 'projectWeekFinish',
        ...columnsCommonConfig.projectWeekFinish,
    },
    {
        header: 'Project Month Start',
        accessorKey: 'projectMonthStart',
        ...columnsCommonConfig.projectMonthStart,
    },
    {
        header: 'Project Month Finish',
        accessorKey: 'projectMonthFinish',
        ...columnsCommonConfig.projectMonthFinish,
    },
    {
        header: 'Last Submitted Status',
        ...columnsCommonConfig.originalStatus,
        accessorKey: 'originalStatus',
        cell: ({ getValue }) => {
            const value = getValue<ISingleSmallCardResponse['originalStatus']>();
            return value?.label || '';
        },
    },
];

const generateFilterByType = (item, activityCodes, activityFragnet, assigneeOptionsList, singleWbs) => {
    switch (item.name) {
        case 'START_DATE_CARD_BOARD':
        case 'END_DATE_CARD_BOARD':
        case 'COMMENT_CARD_BOARD':
        case 'BASELINE_START_DATE_CARD_BOARD':
        case 'BASELINE_END_DATE_CARD_BOARD':
            if (item.data.length === 0) {
                return { ...item, data: [] };
            }
            const newData = item.data
                .map((date) => moment(Number(date)).format(constants.formats.date.defaultForInput))
                .join(' - ');
            return { ...item, data: [newData] };
        case 'TRACKED_CARD_BOARD':
        case 'DOCLINK_CARD_BOARD':
        case 'DELAY_DRIVER_CARD_BOARD':
        case 'IMPORTANT_PREDECESSOR_OVERDUE':
        case 'RCF_RISK':
        case 'CONSTRAINT_DAY_SET':
        case 'LONG_DURATION':
        case 'IMPORTANT_LAG_CARD_30_BOARD':
        case 'OVERDUE_CARD_BOARD':
        case 'FINISH_PROG_OVERDUE_CARD_BOARD':
        case 'START_PROG_OVERDUE_CARD_BOARD':
        case 'SET_BACK':
        case 'IMPORTANT_STALLED':
        case 'CRITICAL_PATH_USER_CARD_BOARD':
            return { ...item, data: [item.data[0] === '1' ? 'Yes' : 'No'] };
        case 'ACTIVITY_CODES':
        case 'ACTIVITY_CODES_EXCLUDE':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return activityCodesTag({ item, activityCodes });
                }),
            };
        case 'ASSIGNEE_CARD_BOARD':
            const assigneeFound = assigneeOptionsList.find((assignee) => assignee.value === item.data[0]);
            return assigneeFound
                ? { ...item, data: [`${assigneeFound.assignee.firstName} ${assigneeFound.assignee.lastName}`] }
                : item;
        case 'UPDATED_USER_CARD_BOARD':
        case 'DURATION_CARD_BOARD':
        case 'LAG_CARD_BOARD':
        case 'TOTAL_FLOAT_CARD_BOARD':
            return item;
        case 'ACTIVITY_TYPE_BOARD':
            return { ...item, data: item.data.map((i) => activityTypeLabels[i]) };
        case 'TYPE_CARD_BOARD':
            return { ...item, data: item.data.map((i) => cardTypeLabels[i]) };
        case 'WBS_LEVEL_CARD_BOARD':
        case 'SUBMITTED_COMPLETION_CARD_BOARD':
            return { ...item, data: [`${item.data[0]} - ${item.data[1]}%`] };
        case 'TAG_CARD_BOARD':
            return { ...item };
        case 'FRAGNET_SEARCH':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return milestoneFragnetTag({ item, milestoneFragnet: activityFragnet });
                }),
            };
        case 'WBS_SEARCH':
            return {
                ...item,
                data: item.data?.map((item) => {
                    return wbsTag({ item, wbs: singleWbs });
                }),
            };
        case 'RESOURCE_NAME_CARD_BOARD':
            return { ...item, data: item.label };
        default:
            return { ...item, data: item.data.map(widgetFilterTitle) };
    }
};

interface IFinalFlags {
    data: string[];
    name: string;
}

export type Flags = {
    endDatesRange?: string;
    startDatesRange?: string;
    commentRange?: string;
    baselineStartDatesRange?: string;
    baselineEndDatesRange?: string;
    isAdvancedTextSearch?: boolean;
};

export const generateFlags = (flags?: Flags) => {
    if (!flags) {
        return [];
    }

    const finalFlags: IFinalFlags[] = [];

    for (const key in flags) {
        const value = typeof flags[key] === 'string' && startCase(lowerCase(flags[key]));
        if (value && flags[key] !== 'FIXED_DATE') {
            if (key === 'endDatesRange') {
                finalFlags.push({ data: [value], name: 'END_DATE_CARD_BOARD' });
            }

            if (key === 'startDatesRange') {
                finalFlags.push({ data: [value], name: 'START_DATE_CARD_BOARD' });
            }

            if (key === 'commentRange') {
                finalFlags.push({ data: [value], name: 'COMMENT_CARD_BOARD' });
            }

            if (key === 'baselineStartDatesRange') {
                finalFlags.push({ data: [value], name: 'BASELINE_START_DATE_CARD_BOARD' });
            }

            if (key === 'baselineEndDatesRange') {
                finalFlags.push({ data: [value], name: 'BASELINE_END_DATE_CARD_BOARD' });
            }
        }
    }

    return finalFlags;
};

//TODO: remove this function and replace with useGlobalFiltersHook
export const buildRowsBoardFilter = ({ filter, activityCodes, activityFragnet, assigneeOptionsList, singleWbs }) => {
    const filtersObject = filter?.filters.hasOwnProperty('cardCachedFilter')
        ? filter?.filters.cardCachedFilter
        : filter?.filters;
    const excludingFilters = ['PROJECT_CARD_BOARD', 'CONTRACT_CARD_BOARD', 'VERSION_CARD_BOARD'];
    const generateFilters = filtersObject.filter.filters.map((item) =>
        generateFilterByType(item, activityCodes, activityFragnet, assigneeOptionsList, singleWbs),
    );

    const flags = generateFlags(filtersObject?.flags);

    return [...flags, ...generateFilters].filter((item) => {
        return !excludingFilters.includes(item.name);
    });
};

export const activityTypeLabels = {
    TASK_DEPENDENT: 'Task',
    START_MILESTONE: 'Milestone',
    FINISH_MILESTONE: 'Milestone',
    WBS: 'WBS',
    LEVEL_OF_EFFORT: 'Level Of Effort(LOE)',
    RESOURCE_DEPENDENT: 'Resource dependent',
};

export const cardTypeLabels = {
    KC_INSIGHT: 'Recommendation',
    PROGRAMME_ACTIVITY: 'Program Activity',
    USER_GENERATED: 'User Action',
};
